import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../api';

const fmt     = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
const fmtNum  = (n) => n != null ? Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR') : '—';

function Field({ label, value }) {
  return (
    <div>
      <p style={{ color: '#94A3B8', fontSize: '0.7rem', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <p style={{ color: '#1E293B', fontSize: '0.875rem', fontWeight: '500', margin: 0 }}>{value || '—'}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E2E8F0', padding: '20px', marginBottom: '16px' }}>
      <p style={{ color: '#94A3B8', fontSize: '0.68rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>
        {title}
      </p>
      {children}
    </div>
  );
}

export default function LocationSignaturePage() {
  const { token } = useParams();
  const canvasRef  = useRef(null);
  const [drawing, setDrawing]       = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [contrat, setContrat]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lastPos = useRef(null);

  useEffect(() => {
    api.getSignatureWeb(token)
      .then(r => setContrat(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  /* ── Canvas ── */
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * (canvas.width / rect.width), y: (src.clientY - rect.top) * (canvas.height / rect.height) };
  };
  const startDraw = (e) => { e.preventDefault(); setDrawing(true); lastPos.current = getPos(e, canvasRef.current); };
  const draw = (e) => {
    e.preventDefault();
    if (!drawing) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(lastPos.current.x, lastPos.current.y); ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1A3A6E'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.stroke();
    lastPos.current = pos;
    setHasSignature(true);
  };
  const stopDraw  = () => setDrawing(false);
  const clearCanvas = () => { canvasRef.current.getContext('2d').clearRect(0, 0, 580, 200); setHasSignature(false); };

  const handleSubmit = async () => {
    if (!hasSignature) return;
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      await api.soumettreSignatureWeb(token, { signataire_nom: contrat?.validation?.signataire_nom || '', signature_image_b64: dataUrl });
      setSubmitted(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur lors de l'envoi de la signature");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── States ── */
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
      <p style={{ color: '#64748B', fontSize: '0.9rem' }}>Chargement…</p>
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FEF2F2' }}>
      <div className="text-center p-8">
        <p style={{ color: '#DC2626', fontSize: '1.1rem', fontWeight: '700' }}>Lien invalide</p>
        <p style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '8px' }}>{error}</p>
      </div>
    </div>
  );
  if (contrat?.deja_signe || submitted) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F0FDF4' }}>
      <div className="text-center p-8 max-w-sm">
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <svg width="32" height="32" fill="none" stroke="#15803D" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <p style={{ color: '#15803D', fontSize: '1.25rem', fontWeight: '700' }}>Contrat signé</p>
        <p style={{ color: '#166534', fontSize: '0.875rem', marginTop: '8px' }}>
          Votre signature a bien été enregistrée pour le contrat <strong>{contrat?.numero}</strong>.
        </p>
      </div>
    </div>
  );

  const lignes   = contrat?.lignes_materiels || [];
  const services = contrat?.lignes_services  || [];

  // Dates prévues : utiliser les dates effectifs si dispo, sinon dériver des lignes matériels
  const dateDebut = contrat.date_debut_effectif
    || (lignes.length > 0
        ? lignes.map(l => l.date_debut).filter(Boolean).sort()[0]
        : null);
  const dateFin = contrat.date_retour_effectif
    || (lignes.length > 0
        ? lignes.map(l => l.date_fin).filter(Boolean).sort().at(-1)
        : null);

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC',  }}>

      {/* Header */}
      <div style={{ background: '#1A3A6E', padding: '16px 24px' }}>
        <p style={{ color: '#fff', fontWeight: '800', fontSize: '1.1rem', margin: 0 }}>Bedee</p>
        <p style={{ color: '#93C5FD', fontSize: '0.75rem', margin: '2px 0 0' }}>Signature électronique — Contrat de location</p>
      </div>

      <div style={{ maxWidth: '680px', margin: '24px auto', padding: '0 16px 40px' }}>

        {/* Bandeau récapitulatif */}
        <div style={{ background: '#1A3A6E', borderRadius: '16px', padding: '16px 20px', marginBottom: '16px', color: '#fff' }}>
          <p style={{ margin: '0 0 4px', fontSize: '0.72rem', color: '#93C5FD', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Contrat à signer
          </p>
          <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', letterSpacing: '-0.02em' }}>{contrat.numero}</p>
          {contrat.titre && (
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#BFDBFE' }}>{contrat.titre}</p>
          )}
        </div>

        {/* Parties */}
        <Section title="Parties">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Field label="Client" value={contrat.client_nom} />
            <Field label="Contact / Signataire" value={contrat.contact_nom || contrat.validation?.signataire_nom} />
            {contrat.ref_client && <Field label="Référence client" value={contrat.ref_client} />}
          </div>
        </Section>

        {/* Période */}
        <Section title="Période de location">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Field label="Date de début" value={fmt(dateDebut)} />
            <Field label="Date de fin prévue" value={fmt(dateFin)} />
            {contrat.adresse_livraison && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Adresse de livraison / chantier" value={contrat.adresse_livraison} />
              </div>
            )}
          </div>
        </Section>

        {/* Matériels */}
        {lignes.length > 0 && (
          <Section title={`Matériels loués (${lignes.length})`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {lignes.map((l, i) => (
                <div key={i} style={{ borderRadius: '10px', border: '1px solid #E2E8F0', padding: '12px 14px', background: '#F8FAFC' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: '700', fontSize: '0.875rem', color: '#1E293B' }}>
                        {l.gamme_nom || l.categorie_nom || `Matériel ${i + 1}`}
                      </p>
                      {l.categorie_nom && l.gamme_nom && (
                        <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748B' }}>{l.categorie_nom}</p>
                      )}
                      {(l.date_debut || l.date_fin) && (
                        <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#64748B' }}>
                          {fmtDate(l.date_debut)} → {fmtDate(l.date_fin)}
                          {l.duree_jours ? ` · ${l.duree_jours} j` : ''}
                        </p>
                      )}
                    </div>
                    {l.montant != null && (
                      <p style={{ margin: 0, fontWeight: '700', fontSize: '0.9rem', color: '#1A3A6E', whiteSpace: 'nowrap' }}>
                        {fmtNum(l.montant)} € HT
                      </p>
                    )}
                  </div>
                  {l.prix_jour_applique != null && (
                    <p style={{ margin: '6px 0 0', fontSize: '0.72rem', color: '#94A3B8' }}>
                      {fmtNum(l.prix_jour_applique)} € / jour
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Services */}
        {services.length > 0 && (
          <Section title="Services">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {services.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#334155' }}>{s.designation || s.libelle || `Service ${i + 1}`}</p>
                  {/* Montant service : montant ou pu selon le type de service */}
                  {(s.montant != null || s.pu != null) && (
                    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#1E293B' }}>
                      {fmtNum(s.montant ?? s.pu)} € HT
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Totaux & conditions */}
        <Section title="Récapitulatif financier">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {contrat.total_location != null && contrat.total_location > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748B' }}>Total location HT</p>
                <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#1E293B' }}>{fmtNum(contrat.total_location)} €</p>
              </div>
            )}
            {contrat.total_ht_services != null && contrat.total_ht_services > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748B' }}>Total services HT</p>
                <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#1E293B' }}>{fmtNum(contrat.total_ht_services)} €</p>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #E2E8F0' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748B' }}>Total HT</p>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '700', color: '#1E293B' }}>{fmtNum(contrat.total_ht)} €</p>
            </div>
            {contrat.total_tva != null && contrat.total_tva > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748B' }}>TVA</p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#1E293B' }}>{fmtNum(contrat.total_tva)} €</p>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '10px', background: '#EFF6FF', marginTop: '4px' }}>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1D4ED8' }}>Total TTC</p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: '800', color: '#1D4ED8' }}>{fmtNum(contrat.total_ttc)} €</p>
            </div>
            {(contrat.mode_reglement || contrat.conditions_paiement_nom) && (
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                {contrat.mode_reglement && <Field label="Mode de règlement" value={contrat.mode_reglement} />}
                {contrat.conditions_paiement_nom && <Field label="Conditions de paiement" value={contrat.conditions_paiement_nom} />}
              </div>
            )}
          </div>
        </Section>

        {/* Notes client */}
        {contrat.notes && (
          <Section title="Notes">
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{contrat.notes}</p>
          </Section>
        )}

        {/* Signature */}
        <div style={{ background: '#fff', borderRadius: '16px', border: '2px solid #1A3A6E', padding: '20px' }}>
          <p style={{ color: '#1A3A6E', fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>
            Signature électronique
          </p>
          <p style={{ color: '#64748B', fontSize: '0.8rem', margin: '0 0 14px' }}>
            En signant ci-dessous, {contrat.validation?.signataire_nom ? <strong>{contrat.validation.signataire_nom}</strong> : 'vous'} reconnaissez avoir pris connaissance
            de l'ensemble des conditions du contrat <strong>{contrat.numero}</strong> et en acceptez les termes.
          </p>

          <div style={{ border: '2px dashed #CBD5E1', borderRadius: '12px', overflow: 'hidden', position: 'relative', touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              width={620}
              height={200}
              style={{ display: 'block', width: '100%', cursor: 'crosshair', background: '#FAFAFA' }}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
            />
            {!hasSignature && (
              <p style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#CBD5E1', fontSize: '0.875rem', pointerEvents: 'none', margin: 0, whiteSpace: 'nowrap' }}>
                Signez ici
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button onClick={clearCanvas}
              style={{ flex: '0 0 auto', padding: '12px 20px', borderRadius: '12px', border: '1px solid #E2E8F0', background: '#F8FAFC', color: '#64748B', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
              Effacer
            </button>
            <button onClick={handleSubmit} disabled={!hasSignature || submitting}
              style={{
                flex: 1, padding: '12px', borderRadius: '12px', border: 'none',
                background: hasSignature ? 'linear-gradient(135deg,#1A3A6E,#1D4ED8)' : '#E2E8F0',
                color: hasSignature ? '#fff' : '#94A3B8', fontSize: '0.9rem', fontWeight: '700',
                cursor: hasSignature ? 'pointer' : 'not-allowed',
              }}>
              {submitting ? 'Envoi…' : 'Valider ma signature'}
            </button>
          </div>
        </div>

        <p style={{ color: '#CBD5E1', fontSize: '0.7rem', textAlign: 'center', marginTop: '20px' }}>
          Document généré par Bedee · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
